import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Lock, ArrowRight, Users, Briefcase, ChevronDown, Plus, Minus, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Password for the page
const PAGE_PASSWORD = 'CultureRises';

// Founder images from Artist pages
const FOUNDER_IMAGES = {
  dan: 'https://images.squarespace-cdn.com/content/v1/6462caf4ffff997cb8dd6e8c/5b2d377b-b781-49fa-890a-b6150d11d6d7/YMHclubafterkiss.jpg',
  selina: 'https://images.squarespace-cdn.com/content/v1/6462caf4ffff997cb8dd6e8c/ad2e90de-946e-498a-a834-6dad961033b9/DSC01241.jpeg'
};

// Accordion Section Component - controlled by parent
const AccordionSection = ({ number, title, children, isOpen, onToggle }) => {
  return (
    <div className="border-b border-white/10">
      <button
        onClick={onToggle}
        className="w-full py-4 md:py-5 px-6 flex items-center justify-between text-left hover:bg-white/[0.02] transition-colors"
      >
        <div className="flex items-center gap-4 md:gap-5">
          <span className="text-primary text-xs tracking-[0.2em] uppercase w-6">{number}</span>
          <h2 className="text-lg md:text-xl lg:text-2xl font-light tracking-tight">{title}</h2>
        </div>
        <div className="text-white/40">
          {isOpen ? <Minus size={20} /> : <Plus size={20} />}
        </div>
      </button>
      
      <div className={`overflow-hidden transition-all duration-500 ease-in-out ${isOpen ? 'max-h-[3000px] opacity-100' : 'max-h-0 opacity-0'}`}>
        <div className="px-6 pb-10 md:pb-12">
          <div className="max-w-3xl ml-10 md:ml-11">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

export const PrivateOverviewPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [openSection, setOpenSection] = useState('01'); // Track which section is open
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    type: 'artist',
    message: ''
  });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Check if already authenticated (session storage)
  useEffect(() => {
    const auth = sessionStorage.getItem('private_overview_auth');
    if (auth === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (password === PAGE_PASSWORD) {
      setIsAuthenticated(true);
      sessionStorage.setItem('private_overview_auth', 'true');
      setError('');
    } else {
      setError('Invalid password');
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const response = await fetch(`${API_URL}/api/private-inquiry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(contactForm)
      });
      
      if (response.ok) {
        setFormSubmitted(true);
      }
    } catch (error) {
      console.error('Submit error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  // Password Gate
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center px-4">
        <Helmet>
          <title>Cultural Revolution | Private Overview</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <Lock className="mx-auto mb-4 text-white/40" size={48} />
            <h1 className="text-2xl font-light tracking-wide text-white mb-2">CULTURAL REVOLUTION</h1>
            <p className="text-white/50 text-sm tracking-widest uppercase">Private Overview</p>
          </div>
          
          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            <div>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                className="w-full px-4 py-3 bg-white/5 border border-white/10 text-white placeholder:text-white/30 focus:outline-none focus:border-white/30 transition-colors"
                autoFocus
              />
              {error && <p className="text-red-400 text-sm mt-2">{error}</p>}
            </div>
            <Button 
              type="submit" 
              className="w-full bg-white text-black hover:bg-white/90 py-3 font-medium tracking-wide"
            >
              ACCESS
            </Button>
          </form>
          
          <p className="text-center text-white/30 text-xs mt-8">
            This page contains confidential information intended for select partners.
          </p>
        </div>
      </div>
    );
  }

  // Main Content
  return (
    <div className="min-h-screen bg-black text-white">
      <Helmet>
        <title>Cultural Revolution | Private Overview</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      {/* HERO */}
      <section className="min-h-screen flex flex-col justify-center items-center text-center px-6 relative border-b border-white/10">
        <div className="max-w-4xl mx-auto">
          <p className="text-white/40 text-xs tracking-[0.3em] uppercase mb-6">Private Overview</p>
          
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-light tracking-tight mb-6">
            Welcome to Cultural Revolution.
          </h1>
          
          <p className="text-lg md:text-xl lg:text-2xl text-white/70 font-light mb-6 leading-relaxed max-w-3xl mx-auto">
            A living ecosystem designed to support Artists across projects, partnerships, and careers.
          </p>
          
          <p className="text-sm md:text-base text-white/50 tracking-widest uppercase">
            This is where the work—and the future—<span className="text-primary">gets built.</span>
          </p>
        </div>
        
        <button 
          onClick={() => scrollToSection('content')}
          className="absolute bottom-12 text-white/30 hover:text-white/60 transition-colors"
        >
          <ChevronDown size={32} className="animate-bounce" />
        </button>
      </section>

      {/* ACCORDION SECTIONS */}
      <div id="content">
        
        {/* 01 - The Moment (with sharpened Why Now) */}
        <AccordionSection 
          number="01" 
          title="The Moment" 
          isOpen={openSection === '01'}
          onToggle={() => setOpenSection(openSection === '01' ? null : '01')}
        >
          <div className="space-y-6 text-white/70 text-lg leading-relaxed">
            <p>
              The creatorsphere and traditional media are no longer separate worlds. They're merging — 
              rapidly, irreversibly, and with massive implications for how culture is created and distributed.
            </p>
            <p>
              Independent creators now command audiences that rival networks. Traditional studios are 
              contracting, creating a vacuum. And AI is lowering production costs, making artist-owned 
              IP more viable than ever.
            </p>
            <p>
              <span className="text-white">This couldn't have worked five years ago. It can now.</span>
            </p>
            <p>
              Cultural Revolution exists at this intersection — taking the best of both worlds: 
              the authenticity and direct connection of the creatorsphere, combined with the production 
              quality and institutional support of traditional media — all in service of the Artist.
            </p>
          </div>
        </AccordionSection>

        {/* 02 - The Problem */}
        <AccordionSection 
          number="02" 
          title="The Problem"
          isOpen={openSection === '02'}
          onToggle={() => setOpenSection(openSection === '02' ? null : '02')}
        >
          <div className="space-y-6 text-white/70 text-lg leading-relaxed">
            <p>
              Independent Artists and companies lack the back-of-the-house infrastructure that 
              traditional studios take for granted. There's no operational support, no staged 
              capital, no long-term alignment.
            </p>
            <p>
              Most Artists are forced to choose: give up control to access resources, or stay 
              independent and struggle alone. They're treated as interchangeable labor — hired 
              for projects, then discarded when the project ends.
            </p>
            <p>
              The result is a system that extracts value from Artists rather than building it 
              alongside them. Careers are fragmented. IP is scattered. Momentum is lost.
            </p>
          </div>
        </AccordionSection>

        {/* 03 - The Thesis */}
        <AccordionSection 
          number="03" 
          title="The Thesis"
          isOpen={openSection === '03'}
          onToggle={() => setOpenSection(openSection === '03' ? null : '03')}
        >
          <blockquote className="text-2xl md:text-3xl font-light text-white mb-8 leading-snug">
            "Artists should be treated like founders — not freelancers."
          </blockquote>
          <div className="space-y-6 text-white/70 text-lg leading-relaxed">
            <p>
              Just as startups receive staged capital and long-term support, Artists deserve the same. 
              Not one-off deals, but sustained investment in their careers. Not short-term extraction, 
              but long-term alignment.
            </p>
            <p>
              This means providing capital when it's needed, operational support to execute, and 
              strategic guidance to scale. It means treating each Artist's career as a portfolio 
              of assets to be built over time — not a series of disconnected transactions.
            </p>
            <p>
              When Artists win, everyone wins. That's the alignment we're building.
            </p>
          </div>
        </AccordionSection>

        {/* 04 - What We Are (Differentiation) */}
        <AccordionSection 
          number="04" 
          title="What We Are"
          isOpen={openSection === '04'}
          onToggle={() => setOpenSection(openSection === '04' ? null : '04')}
        >
          <div className="space-y-6 text-white/70 text-lg leading-relaxed mb-8">
            <p className="text-white text-xl">
              We're not a talent agency. We're not a studio. We're not investors who take a piece and disappear.
            </p>
            <p>
              We're the infrastructure layer that lets Artists operate like founders — without giving up control.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div className="p-6 border border-white/10">
              <h3 className="text-white text-lg mb-3">Pre-Seed & Seed Support</h3>
              <p className="text-white/60 text-sm leading-relaxed">
                Early-stage capital and resources for Artists developing their first or next major project.
              </p>
            </div>
            <div className="p-6 border border-white/10">
              <h3 className="text-white text-lg mb-3">Staged Financing</h3>
              <p className="text-white/60 text-sm leading-relaxed">
                Tranched capital deployment that grows with the Artist's career and project milestones.
              </p>
            </div>
            <div className="p-6 border border-white/10">
              <h3 className="text-white text-lg mb-3">Minority Ownership</h3>
              <p className="text-white/60 text-sm leading-relaxed">
                Long-term alignment through equity participation, not ownership. Artists retain control.
              </p>
            </div>
            <div className="p-6 border border-white/10">
              <h3 className="text-white text-lg mb-3">Production & Distribution</h3>
              <p className="text-white/60 text-sm leading-relaxed">
                End-to-end support from development through release, with in-house distribution capabilities.
              </p>
            </div>
          </div>
          <div className="p-6 border border-white/10">
            <h3 className="text-white text-lg mb-3">Narrative Support</h3>
            <p className="text-white/60 text-sm leading-relaxed">
              Strategic communications, press, and positioning to build lasting cultural relevance.
            </p>
          </div>
        </AccordionSection>

        {/* 05 - The Artist Founders */}
        <AccordionSection 
          number="05" 
          title="The Artist Founders"
          isOpen={openSection === '05'}
          onToggle={() => setOpenSection(openSection === '05' ? null : '05')}
        >
          <div className="space-y-6 text-white/70 text-lg leading-relaxed mb-12">
            <p>
              Cultural Revolution was founded by <span className="text-white">Dan Levy Dagerman</span> and <span className="text-white">Selina Ringel</span>—two 
              Artist-Founders who have spent their careers writing, producing, marketing, and distributing 
              independent work.
            </p>
            <p>
              Through that experience, they repeatedly encountered the same challenge: every new project 
              meant rebuilding infrastructure from scratch. Legal, financial, distribution, marketing—each 
              time, starting over.
            </p>
            <p>
              Cultural Revolution emerged from a simple question: <span className="text-white italic">What if that infrastructure could 
              compound instead of disappear?</span>
            </p>
            <p>
              The result is an ecosystem that treats Artists like founders—providing the back-of-the-house 
              support that allows creative work to scale across projects, partnerships, and careers.
            </p>
          </div>
          
          <div className="space-y-12">
            <div className="flex flex-col md:flex-row gap-8 items-start">
              <div className="w-28 h-28 flex-shrink-0 overflow-hidden">
                <img 
                  src={FOUNDER_IMAGES.dan} 
                  alt="Dan Levy Dagerman" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <h3 className="text-xl text-white mb-2">Dan Levy Dagerman</h3>
                <p className="text-white/40 text-sm uppercase tracking-wider mb-4">Artist-Founder</p>
                <p className="text-white/60 leading-relaxed">
                  Filmmaker, writer, and entrepreneur whose experience spans creative development, production, 
                  marketing, and distribution of independent work. His journey through the operational 
                  challenges of building sustainable creative careers shaped the ecosystem.
                </p>
              </div>
            </div>
            
            <div className="flex flex-col md:flex-row gap-8 items-start">
              <div className="w-28 h-28 flex-shrink-0 overflow-hidden">
                <img 
                  src={FOUNDER_IMAGES.selina} 
                  alt="Selina Ringel" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <h3 className="text-xl text-white mb-2">Selina Ringel</h3>
                <p className="text-white/40 text-sm uppercase tracking-wider mb-4">Artist-Founder</p>
                <p className="text-white/60 leading-relaxed">
                  Producer and strategist with deep experience across content development, talent relations, 
                  and the business of independent media. Her operational expertise and network built across 
                  film, television, and digital platforms form a critical foundation of the ecosystem.
                </p>
              </div>
            </div>
          </div>
        </AccordionSection>

        {/* 06 - The Ecosystem */}
        <AccordionSection 
          number="06" 
          title="The Ecosystem"
          isOpen={openSection === '06'}
          onToggle={() => setOpenSection(openSection === '06' ? null : '06')}
        >
          <p className="text-white/70 text-lg leading-relaxed mb-8">
            The ecosystem expands outward from artists' work. Cultural Revolution is the connective 
            tissue—not the top-down controller.
          </p>

          <div className="grid md:grid-cols-4 gap-4">
            <div className="p-5 border border-white/10 hover:border-primary/30 transition-colors">
              <div className="text-xs text-primary uppercase tracking-widest mb-2">01</div>
              <h4 className="text-white mb-1">Artists</h4>
              <p className="text-white/40 text-sm">Vision, voice, and the work itself.</p>
            </div>
            <div className="p-5 border border-white/10 hover:border-primary/30 transition-colors">
              <div className="text-xs text-primary uppercase tracking-widest mb-2">02</div>
              <h4 className="text-white mb-1">Projects</h4>
              <p className="text-white/40 text-sm">Films, series, and content that emerge.</p>
            </div>
            <div className="p-5 border border-white/10 hover:border-primary/30 transition-colors">
              <div className="text-xs text-primary uppercase tracking-widest mb-2">03</div>
              <h4 className="text-white mb-1">Companies</h4>
              <p className="text-white/40 text-sm">Entities that scale and sustain the work.</p>
            </div>
            <div className="p-5 border border-primary/30 bg-primary/5">
              <div className="text-xs text-primary uppercase tracking-widest mb-2">∞</div>
              <h4 className="text-white mb-1">Infrastructure</h4>
              <p className="text-white/40 text-sm">Cultural Revolution connects it all.</p>
            </div>
          </div>
        </AccordionSection>

        {/* 07 - In Motion (Social Proof / Traction) */}
        <AccordionSection 
          number="07" 
          title="In Motion"
          isOpen={openSection === '07'}
          onToggle={() => setOpenSection(openSection === '07' ? null : '07')}
        >
          <div className="space-y-6 text-white/70 text-lg leading-relaxed mb-8">
            <p>
              The ecosystem isn't a pitch — it's already running. <span className="text-white">CulturalRevolution.io</span> is 
              the living proof: a platform that aggregates and amplifies the work as it happens.
            </p>
            <p>
              Every project, every press hit, every release builds on the last. The flywheel is turning.
            </p>
          </div>

          {/* Live Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="p-4 bg-white/5 text-center">
              <p className="text-3xl text-white mb-1">2</p>
              <p className="text-white/40 text-xs uppercase tracking-wider">Artists</p>
            </div>
            <div className="p-4 bg-white/5 text-center">
              <p className="text-3xl text-white mb-1">2</p>
              <p className="text-white/40 text-xs uppercase tracking-wider">Projects</p>
            </div>
            <div className="p-4 bg-white/5 text-center">
              <p className="text-3xl text-white mb-1">4</p>
              <p className="text-white/40 text-xs uppercase tracking-wider">Companies</p>
            </div>
            <div className="p-4 bg-white/5 text-center">
              <p className="text-3xl text-white mb-1">12+</p>
              <p className="text-white/40 text-xs uppercase tracking-wider">Press & Updates</p>
            </div>
          </div>

          {/* Recent Activity */}
          <div className="border border-white/10 p-6">
            <h4 className="text-white text-sm uppercase tracking-wider mb-4">Recent Activity</h4>
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <Calendar size={16} className="text-primary mt-1 flex-shrink-0" />
                <div>
                  <p className="text-white">You, Me & Her — Now Streaming</p>
                  <p className="text-white/40 text-sm">Feature film, distribution secured across platforms</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Calendar size={16} className="text-primary mt-1 flex-shrink-0" />
                <div>
                  <p className="text-white">Variety, IndieWire, Hollywood Reporter Coverage</p>
                  <p className="text-white/40 text-sm">Trade press validation for distribution innovation</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Calendar size={16} className="text-primary mt-1 flex-shrink-0" />
                <div>
                  <p className="text-white">High Concept + High Times: Cinema Sessions</p>
                  <p className="text-white/40 text-sm">Brand partnership expanding reach into cannabis culture</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Calendar size={16} className="text-primary mt-1 flex-shrink-0" />
                <div>
                  <p className="text-white">Space Weed Universe — In Development</p>
                  <p className="text-white/40 text-sm">Multi-format IP with film, series, and merchandise potential</p>
                </div>
              </div>
            </div>
          </div>

          <p className="text-white/50 text-sm mt-6 italic">
            Visit <a href="https://culturalrevolution.io" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">CulturalRevolution.io</a> to see the ecosystem in action.
          </p>
        </AccordionSection>

        {/* 08 - Distribution Stack */}
        <AccordionSection 
          number="08" 
          title="Distribution Stack"
          isOpen={openSection === '08'}
          onToggle={() => setOpenSection(openSection === '08' ? null : '08')}
        >
          <p className="text-white/70 text-lg leading-relaxed mb-8">
            Through our internal projects we're building capabilities across the entire stack to maximize reach and revenue.
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="p-6 border border-white/10">
              <h3 className="text-white mb-3">Theatrical Alignment</h3>
              <p className="text-white/50 text-sm leading-relaxed">
                Strategic partnerships with theatrical distributors for projects that benefit from 
                the cultural impact of theatrical release.
              </p>
            </div>
            <div className="p-6 border border-white/10">
              <h3 className="text-white mb-3">TVOD & Streaming</h3>
              <p className="text-white/50 text-sm leading-relaxed">
                In-house handling of transactional video-on-demand and streaming platform placement 
                to optimize revenue windows.
              </p>
            </div>
            <div className="p-6 border border-white/10 md:col-span-2">
              <h3 className="text-white mb-3">Direct Distribution</h3>
              <p className="text-white/50 text-sm leading-relaxed">
                Building direct relationships with audiences through owned channels, community 
                engagement, and innovative release strategies that bypass traditional gatekeepers.
              </p>
            </div>
          </div>
        </AccordionSection>

        {/* 09 - The Social Cinema Model */}
        <AccordionSection 
          number="09" 
          title="The Social Cinema Model"
          isOpen={openSection === '09'}
          onToggle={() => setOpenSection(openSection === '09' ? null : '09')}
        >
          <div className="space-y-6 text-white/70 text-lg leading-relaxed">
            <p>
              The Social Cinema Model integrates creation, conversation, and distribution into a single 
              continuous loop—supported by Cultural Revolution's back-of-the-house infrastructure.
            </p>
            <p>
              Artists are not expected to do this alone. Cultural Revolution handles the systems, strategy, 
              and continuity so Artists can focus on making the work while audiences grow alongside it.
            </p>
            <p>
              <span className="text-white">This is how projects build momentum, careers compound, and culture sustains itself over time.</span>
            </p>
          </div>
        </AccordionSection>

        {/* 10 - Flywheel */}
        <AccordionSection 
          number="10" 
          title="Flywheel"
          isOpen={openSection === '10'}
          onToggle={() => setOpenSection(openSection === '10' ? null : '10')}
        >
          <p className="text-white/70 text-lg leading-relaxed mb-8">
            Cultural Revolution operates as a flywheel — each component reinforcing the others, 
            building momentum over time.
          </p>
          
          <div className="relative">
            <div className="absolute left-4 top-0 bottom-0 w-[1px] bg-white/10"></div>
            <div className="space-y-6">
              <div className="pl-12 relative">
                <div className="absolute left-2 w-4 h-4 rounded-full bg-primary"></div>
                <h3 className="text-white mb-1">Artists Enter</h3>
                <p className="text-white/50 text-sm">Talented Artists join the ecosystem with their vision and IP.</p>
              </div>
              <div className="pl-12 relative">
                <div className="absolute left-2 w-4 h-4 rounded-full bg-primary/80"></div>
                <h3 className="text-white mb-1">Projects Are Built</h3>
                <p className="text-white/50 text-sm">Infrastructure and capital support bring projects to life.</p>
              </div>
              <div className="pl-12 relative">
                <div className="absolute left-2 w-4 h-4 rounded-full bg-primary/60"></div>
                <h3 className="text-white mb-1">IP Scales</h3>
                <p className="text-white/50 text-sm">Successful projects expand across formats and platforms.</p>
              </div>
              <div className="pl-12 relative">
                <div className="absolute left-2 w-4 h-4 rounded-full bg-primary/40"></div>
                <h3 className="text-white mb-1">Revenue Flows Back</h3>
                <p className="text-white/50 text-sm">Returns are generated across distribution windows.</p>
              </div>
              <div className="pl-12 relative">
                <div className="absolute left-2 w-4 h-4 rounded-full bg-primary/20"></div>
                <h3 className="text-white mb-1">Capital Is Reinvested</h3>
                <p className="text-white/50 text-sm">Profits fuel the next generation of Artists and projects.</p>
              </div>
            </div>
          </div>
        </AccordionSection>

        {/* 11 - Revenue & Value Flows */}
        <AccordionSection 
          number="11" 
          title="Revenue & Value Flows"
          isOpen={openSection === '11'}
          onToggle={() => setOpenSection(openSection === '11' ? null : '11')}
        >
          <p className="text-white/70 text-lg leading-relaxed mb-8">
            Value creation at Cultural Revolution is iterative, strategic, and compounding. 
            Multiple revenue streams reinforce each other.
          </p>
          
          <div className="space-y-3">
            <div className="flex items-center gap-4 p-4 border border-white/10 hover:border-white/20 transition-colors">
              <span className="text-primary text-sm w-8">01</span>
              <div>
                <h3 className="text-white">Projects</h3>
                <p className="text-white/40 text-sm">Box office, streaming deals, licensing</p>
              </div>
            </div>
            <div className="flex items-center gap-4 p-4 border border-white/10 hover:border-white/20 transition-colors">
              <span className="text-primary text-sm w-8">02</span>
              <div>
                <h3 className="text-white">Brands & Partnerships</h3>
                <p className="text-white/40 text-sm">Sponsorships, integrations, co-productions</p>
              </div>
            </div>
            <div className="flex items-center gap-4 p-4 border border-white/10 hover:border-white/20 transition-colors">
              <span className="text-primary text-sm w-8">03</span>
              <div>
                <h3 className="text-white">Merchandise & DTC</h3>
                <p className="text-white/40 text-sm">Direct-to-consumer products, collectibles, experiences</p>
              </div>
            </div>
            <div className="flex items-center gap-4 p-4 border border-white/10 hover:border-white/20 transition-colors">
              <span className="text-primary text-sm w-8">04</span>
              <div>
                <h3 className="text-white">Media & Education</h3>
                <p className="text-white/40 text-sm">Courses, content, community subscriptions</p>
              </div>
            </div>
            <div className="flex items-center gap-4 p-4 border border-white/10 hover:border-white/20 transition-colors">
              <span className="text-primary text-sm w-8">05</span>
              <div>
                <h3 className="text-white">Infrastructure</h3>
                <p className="text-white/40 text-sm">Service fees, platform revenue</p>
              </div>
            </div>
            <div className="flex items-center gap-4 p-4 border border-primary/30 bg-primary/5">
              <span className="text-primary text-sm w-8">→</span>
              <div>
                <h3 className="text-white">Reinvestment</h3>
                <p className="text-white/40 text-sm">Compounding returns into new Artists and projects</p>
              </div>
            </div>
          </div>
        </AccordionSection>

        {/* 12 - Expansion & Partnerships */}
        <AccordionSection 
          number="12" 
          title="Expansion & Partnerships"
          isOpen={openSection === '12'}
          onToggle={() => setOpenSection(openSection === '12' ? null : '12')}
        >
          <p className="text-white/70 text-lg leading-relaxed mb-8">
            Cultural Revolution is designed to grow beyond its founding Artists. We actively 
            seek partnerships with aligned parties across the creative landscape.
          </p>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="p-6 border border-white/10">
              <h3 className="text-white mb-3">External Artists</h3>
              <p className="text-white/50 text-sm leading-relaxed">
                Artists at any stage who share our values and could benefit from infrastructure 
                and long-term support.
              </p>
            </div>
            <div className="p-6 border border-white/10">
              <h3 className="text-white mb-3">Projects</h3>
              <p className="text-white/50 text-sm leading-relaxed">
                Compelling IP and projects that fit within our development and distribution capabilities.
              </p>
            </div>
            <div className="p-6 border border-white/10">
              <h3 className="text-white mb-3">Companies</h3>
              <p className="text-white/50 text-sm leading-relaxed">
                Production companies, studios, and platforms seeking strategic collaboration.
              </p>
            </div>
            <div className="p-6 border border-white/10">
              <h3 className="text-white mb-3">Aligned Technology</h3>
              <p className="text-white/50 text-sm leading-relaxed">
                Disruptive technologies that can enhance how content is created, distributed, or monetized.
              </p>
            </div>
          </div>
        </AccordionSection>

        {/* 13 - Let's Talk */}
        <AccordionSection 
          number="13" 
          title="Let's Talk"
          isOpen={openSection === '13'}
          onToggle={() => setOpenSection(openSection === '13' ? null : '13')}
        >
          <p className="text-white/70 text-lg leading-relaxed mb-8 text-center">
            Cultural Revolution is built on relationships. If what you've read resonates, 
            we'd welcome a conversation.
          </p>
          
          <div className="flex flex-col md:flex-row gap-4 justify-center">
            <button 
              onClick={() => {
                setContactForm(prev => ({ ...prev, type: 'artist' }));
                scrollToSection('contact');
              }}
              className="flex items-center justify-center gap-3 px-8 py-4 bg-white text-black hover:bg-white/90 transition-colors"
            >
              <Users size={20} />
              <span>Artists — Tell Us About Your Work</span>
            </button>
            <button 
              onClick={() => {
                setContactForm(prev => ({ ...prev, type: 'partner' }));
                scrollToSection('contact');
              }}
              className="flex items-center justify-center gap-3 px-8 py-4 border border-white/30 text-white hover:bg-white/5 transition-colors"
            >
              <Briefcase size={20} />
              <span>Partners — Explore Collaboration</span>
            </button>
            <button 
              onClick={() => {
                setContactForm(prev => ({ ...prev, type: 'investor' }));
                scrollToSection('contact');
              }}
              className="flex items-center justify-center gap-3 px-8 py-4 border border-white/30 text-white hover:bg-white/5 transition-colors"
            >
              <Calendar size={20} />
              <span>Investors — Schedule a Call</span>
            </button>
          </div>
        </AccordionSection>

      </div>

      {/* Contact Form */}
      <section id="contact" className="py-24 md:py-32 px-6 border-t border-white/10">
        <div className="max-w-xl mx-auto">
          <h2 className="text-2xl font-light mb-8 text-center">Get in Touch</h2>
          
          {formSubmitted ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-xl text-white mb-2">Message Received</h3>
              <p className="text-white/50">
                Thank you for reaching out. We'll be in touch soon.
              </p>
            </div>
          ) : (
            <form onSubmit={handleContactSubmit} className="space-y-6">
              <div>
                <label className="block text-white/50 text-sm mb-2">I am a(n)</label>
                <div className="flex gap-3">
                  {[
                    { value: 'artist', label: 'Artist' },
                    { value: 'partner', label: 'Company/Partner' },
                    { value: 'investor', label: 'Investor' }
                  ].map(option => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setContactForm(prev => ({ ...prev, type: option.value }))}
                      className={`flex-1 py-2 px-4 text-sm transition-colors ${
                        contactForm.type === option.value
                          ? 'bg-white text-black'
                          : 'border border-white/20 text-white/70 hover:border-white/40'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="block text-white/50 text-sm mb-2">Name</label>
                <input
                  type="text"
                  required
                  value={contactForm.name}
                  onChange={(e) => setContactForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 text-white placeholder:text-white/30 focus:outline-none focus:border-white/30"
                  placeholder="Your name"
                />
              </div>
              
              <div>
                <label className="block text-white/50 text-sm mb-2">Email</label>
                <input
                  type="email"
                  required
                  value={contactForm.email}
                  onChange={(e) => setContactForm(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 text-white placeholder:text-white/30 focus:outline-none focus:border-white/30"
                  placeholder="your@email.com"
                />
              </div>
              
              <div>
                <label className="block text-white/50 text-sm mb-2">Message</label>
                <textarea
                  required
                  value={contactForm.message}
                  onChange={(e) => setContactForm(prev => ({ ...prev, message: e.target.value }))}
                  rows={5}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 text-white placeholder:text-white/30 focus:outline-none focus:border-white/30 resize-none"
                  placeholder="Tell us about yourself and what brought you here..."
                />
              </div>
              
              <Button 
                type="submit" 
                disabled={submitting}
                className="w-full bg-white text-black hover:bg-white/90 py-4 font-medium tracking-wide"
              >
                {submitting ? 'Sending...' : 'Send Message'}
                <ArrowRight size={16} className="ml-2" />
              </Button>
            </form>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-white/10 text-center">
        <p className="text-white/30 text-sm">
          This document is confidential and intended for the recipient only.
        </p>
        <p className="text-white/20 text-xs mt-2">
          © {new Date().getFullYear()} Cultural Revolution
        </p>
      </footer>
    </div>
  );
};

export default PrivateOverviewPage;
